import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  whatsappMailboxService,
  WhatsAppMailboxFilters,
  CreateWhatsAppMailboxRequest,
  UpdateWhatsAppMailboxRequest,
} from '@services/whatsapp-mailbox.service'

export function useWhatsAppMailboxes(filters: WhatsAppMailboxFilters = {}) {
  return useQuery({
    queryKey: ['whatsapp-mailboxes', filters],
    queryFn: () => whatsappMailboxService.fetchWhatsAppMailboxes(filters),
  })
}

export function useWhatsAppMailbox(id: number) {
  return useQuery({
    queryKey: ['whatsapp-mailbox', id],
    queryFn: () => whatsappMailboxService.getWhatsAppMailbox(id),
    enabled: !!id,
  })
}

export function useWhatsAppMailboxStats() {
  return useQuery({
    queryKey: ['whatsapp-mailboxes', 'stats'],
    queryFn: whatsappMailboxService.getStats,
    refetchInterval: 60000, // Refresh every minute
  })
}

export function useWhatsAppMailboxHealth(id: number) {
  return useQuery({
    queryKey: ['whatsapp-mailbox', id, 'health'],
    queryFn: () => whatsappMailboxService.getHealth(id),
    enabled: !!id,
    refetchInterval: 300000, // Refresh every 5 minutes
  })
}

export function useCreateWhatsAppMailbox() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateWhatsAppMailboxRequest) => whatsappMailboxService.createWhatsAppMailbox(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp-mailboxes'] })
      queryClient.invalidateQueries({ queryKey: ['whatsapp-mailboxes', 'stats'] })
    },
  })
}

export function useUpdateWhatsAppMailbox(id: number) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: UpdateWhatsAppMailboxRequest) => whatsappMailboxService.updateWhatsAppMailbox(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp-mailboxes'] })
      queryClient.invalidateQueries({ queryKey: ['whatsapp-mailbox', id] })
      queryClient.invalidateQueries({ queryKey: ['whatsapp-mailboxes', 'stats'] })
    },
  })
}

export function useDeleteWhatsAppMailbox() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => whatsappMailboxService.deleteWhatsAppMailbox(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp-mailboxes'] })
      queryClient.invalidateQueries({ queryKey: ['whatsapp-mailboxes', 'stats'] })
    },
  })
}

export function useResetDailyMessageCount() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => whatsappMailboxService.resetDailyMessageCount(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp-mailboxes'] })
      queryClient.invalidateQueries({ queryKey: ['whatsapp-mailbox', id] })
      queryClient.invalidateQueries({ queryKey: ['whatsapp-mailboxes', 'stats'] })
    },
  })
}
